import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const GaleriaFotos = () => {
  const { API, user } = useContext(AuthContext);
  const [albumes, setAlbumes] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [fotosRaiz, setFotosRaiz] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fotoSeleccionada, setFotoSeleccionada] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({ api_key: '', folder_id: '' });
  const [savingConfig, setSavingConfig] = useState(false);
  const [noConfigurado, setNoConfigurado] = useState(false);
  // Navigation stack for subfolder browsing
  const [navStack, setNavStack] = useState([]); // [{id, nombre}]
  const [currentFolder, setCurrentFolder] = useState(null); // null = root
  const [subcarpetas, setSubcarpetas] = useState([]);
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('token') || localStorage.getItem('access_token');
  const isAdmin = user?.rol === 'admin';

  useEffect(() => { 
    loadAlbumes(); 
    if (isAdmin) loadConfig();
  }, []);

  const loadAlbumes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/galeria/albumes`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setAlbumes(res.data.albumes || []);
      setFotosRaiz(res.data.fotos_raiz || []);
      setNoConfigurado(false);
    } catch (err) {
      if (err.response?.status === 500 && err.response?.data?.detail?.includes('no configurado')) {
        setNoConfigurado(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await axios.get(`${API}/galeria/config`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setConfig({ api_key: res.data.api_key || '', folder_id: res.data.folder_id || '' });
    } catch (err) { /* ignore */ }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${API}/galeria/config`, config, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setShowConfig(false);
      setNoConfigurado(false);
      loadAlbumes();
    } catch (err) {
      toast.error('Error guardando configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const abrirAlbum = async (albumId, albumNombre) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/galeria/album/${albumId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setFotos(res.data.fotos || []);
      setSubcarpetas(res.data.subcarpetas || []);
      
      if (currentFolder === null) {
        // Coming from root
        setNavStack([{ id: albumId, nombre: albumNombre || res.data.album_nombre }]);
      } else {
        // Going deeper
        setNavStack(prev => [...prev, { id: albumId, nombre: albumNombre || res.data.album_nombre }]);
      }
      setCurrentFolder(albumId);
    } catch (err) {
      toast.error('Error cargando álbum');
    } finally {
      setLoading(false);
    }
  };

  const navegarA = async (index) => {
    if (index === -1) {
      // Go back to root
      setCurrentFolder(null);
      setNavStack([]);
      setFotos([]);
      setSubcarpetas([]);
      setFotoSeleccionada(null);
      return;
    }
    const target = navStack[index];
    setNavStack(prev => prev.slice(0, index + 1));
    setLoading(true);
    try {
      const res = await axios.get(`${API}/galeria/album/${target.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setFotos(res.data.fotos || []);
      setSubcarpetas(res.data.subcarpetas || []);
      setCurrentFolder(target.id);
    } catch (err) {
      toast.error('Error navegando');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    const folderId = currentFolder || config.folder_id;
    if (!folderId) { toast.error('No hay carpeta seleccionada'); return; }
    
    setUploading(true);
    setUploadProgress(0);
    let uploaded = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/galeria/upload/${folderId}`, formData, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        });
        uploaded++;
        setUploadProgress(Math.round((uploaded / files.length) * 100));
      } catch (err) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    
    toast.success(`${uploaded} de ${files.length} fotos subidas`);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    
    // Reload current view
    if (currentFolder) {
      abrirAlbum(currentFolder, navStack[navStack.length - 1]?.nombre);
      // Remove last nav entry since abrirAlbum adds one
      setNavStack(prev => prev.slice(0, -1));
    } else {
      loadAlbumes();
    }
  };

  const crearCarpeta = async () => {
    if (!newFolderName.trim()) return;
    const parentId = currentFolder || config.folder_id;
    if (!parentId) { toast.error('No hay carpeta padre'); return; }
    
    try {
      await axios.post(`${API}/galeria/crear-carpeta/${parentId}`, 
        { nombre: newFolderName.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Carpeta "${newFolderName}" creada`);
      setNewFolderName('');
      setShowNewFolder(false);
      // Reload
      if (currentFolder) {
        const lastNav = navStack[navStack.length - 1];
        setNavStack(prev => prev.slice(0, -1));
        abrirAlbum(currentFolder, lastNav?.nombre);
      } else {
        loadAlbumes();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando carpeta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-3xl text-blue-500 mb-3" />
          <p className="text-gray-500">Cargando galería...</p>
        </div>
      </div>
    );
  }

  // Lightbox
  if (fotoSeleccionada) {
    const allFotos = currentFolder ? fotos : fotosRaiz;
    const idx = allFotos.findIndex(f => f.id === fotoSeleccionada.id);
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setFotoSeleccionada(null)} data-testid="lightbox">
        <button className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl z-50" onClick={() => setFotoSeleccionada(null)} data-testid="lightbox-close">
          <i className="fas fa-times" />
        </button>
        {idx > 0 && (
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-3xl z-50"
            onClick={(e) => { e.stopPropagation(); setFotoSeleccionada(allFotos[idx - 1]); }} data-testid="lightbox-prev">
            <i className="fas fa-chevron-left" />
          </button>
        )}
        {idx < allFotos.length - 1 && (
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-3xl z-50"
            onClick={(e) => { e.stopPropagation(); setFotoSeleccionada(allFotos[idx + 1]); }} data-testid="lightbox-next">
            <i className="fas fa-chevron-right" />
          </button>
        )}
        <div className="max-w-5xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
          <img src={fotoSeleccionada.imagen_url} alt={fotoSeleccionada.nombre}
            className="max-w-full max-h-[80vh] object-contain rounded-lg mx-auto" />
          <div className="flex items-center justify-between mt-3 px-2">
            <p className="text-white/80 text-sm truncate">{fotoSeleccionada.nombre}</p>
            <a href={fotoSeleccionada.download_url} target="_blank" rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1" data-testid="lightbox-download">
              <i className="fas fa-download" /> Descargar
            </a>
          </div>
          <p className="text-white/40 text-xs text-center mt-1">{idx + 1} / {allFotos.length}</p>
        </div>
      </div>
    );
  }

  // Inside a folder (album/subfolder view)
  if (currentFolder) {
    const currentName = navStack[navStack.length - 1]?.nombre || 'Carpeta';
    return (
      <div data-testid="galeria-album-view">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-1.5 mb-4 text-sm flex-wrap" data-testid="breadcrumbs">
          <button onClick={() => navegarA(-1)} className="text-blue-600 hover:text-blue-800 font-medium">
            <i className="fas fa-images mr-1" /> Galería
          </button>
          {navStack.map((item, i) => (
            <React.Fragment key={item.id}>
              <i className="fas fa-chevron-right text-gray-300 text-xs" />
              {i === navStack.length - 1 ? (
                <span className="text-gray-700 font-semibold">{item.nombre}</span>
              ) : (
                <button onClick={() => navegarA(i)} className="text-blue-600 hover:text-blue-800">{item.nombre}</button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Header with actions */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navStack.length > 1 ? navegarA(navStack.length - 2) : navegarA(-1)} 
              className="text-gray-600" data-testid="btn-volver-albumes">
              <i className="fas fa-arrow-left mr-2" /> Volver
            </Button>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{currentName}</h2>
              <p className="text-sm text-gray-500">
                {subcarpetas.length > 0 && `${subcarpetas.length} carpetas · `}{fotos.length} fotos
              </p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <input type="file" ref={fileInputRef} multiple accept="image/*" className="hidden" onChange={handleUpload} data-testid="input-upload-foto" />
              <Button size="sm" variant="outline" onClick={() => setShowNewFolder(!showNewFolder)} data-testid="btn-nueva-carpeta">
                <i className="fas fa-folder-plus mr-1" /> Nueva Carpeta
              </Button>
              <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-subir-fotos">
                {uploading ? <><i className="fas fa-spinner fa-spin mr-1" /> {uploadProgress}%</> : <><i className="fas fa-upload mr-1" /> Subir Fotos</>}
              </Button>
            </div>
          )}
        </div>

        {/* New folder input */}
        {showNewFolder && isAdmin && (
          <div className="flex items-center gap-2 mb-4 bg-blue-50 p-3 rounded-lg" data-testid="new-folder-input">
            <i className="fas fa-folder-plus text-blue-500" />
            <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nombre de la carpeta..." className="flex-1 px-3 py-1.5 border rounded-lg text-sm"
              onKeyDown={(e) => e.key === 'Enter' && crearCarpeta()} data-testid="input-nombre-carpeta" />
            <Button size="sm" onClick={crearCarpeta} data-testid="btn-crear-carpeta">Crear</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>Cancelar</Button>
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div className="mb-4 bg-blue-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-blue-700 mb-2">
              <i className="fas fa-cloud-upload-alt" /> Subiendo fotos... {uploadProgress}%
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {/* Subfolders */}
        {subcarpetas.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              <i className="fas fa-folder mr-1" /> Carpetas
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {subcarpetas.map((sub) => (
                <Card key={sub.id} className="group overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  onClick={() => abrirAlbum(sub.id, sub.nombre)} data-testid={`subcarpeta-${sub.id}`}>
                  <div className="relative h-36 bg-gradient-to-br from-blue-100 to-blue-200 overflow-hidden">
                    {sub.portada_url ? (
                      <img src={sub.portada_url} alt={sub.nombre} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="fas fa-folder text-4xl text-blue-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-white font-bold leading-tight">{sub.nombre}</h3>
                      <p className="text-white/70 text-xs mt-0.5"><i className="fas fa-image mr-1" /> {sub.num_fotos} fotos</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        {fotos.length > 0 && (
          <div>
            {subcarpetas.length > 0 && (
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                <i className="fas fa-image mr-1" /> Fotos
              </h3>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {fotos.map((foto) => (
                <div key={foto.id}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer bg-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                  onClick={() => setFotoSeleccionada(foto)} data-testid={`foto-${foto.id}`}>
                  <img src={foto.thumbnail_url} alt={foto.nombre}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-xs truncate">{foto.nombre}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {fotos.length === 0 && subcarpetas.length === 0 && (
          <Card><CardContent className="p-12 text-center">
            <i className="fas fa-image text-4xl text-gray-300 mb-3" />
            <p className="text-gray-500">Esta carpeta está vacía</p>
            {isAdmin && <p className="text-sm text-gray-400 mt-1">Usá los botones de arriba para subir fotos o crear subcarpetas</p>}
          </CardContent></Card>
        )}
      </div>
    );
  }

  // ROOT - Albums grid
  return (
    <div data-testid="galeria-albumes-view">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 text-white">
            <i className="fas fa-images text-lg" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Galería IBA</h2>
            <p className="text-sm text-gray-500">Memorias y eventos del instituto</p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <input type="file" ref={fileInputRef} multiple accept="image/*" className="hidden" onChange={handleUpload} />
            <Button size="sm" variant="outline" onClick={() => setShowNewFolder(!showNewFolder)} data-testid="btn-nueva-carpeta-root">
              <i className="fas fa-folder-plus mr-1" /> Nueva Carpeta
            </Button>
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-subir-fotos-root">
              {uploading ? <><i className="fas fa-spinner fa-spin mr-1" /> {uploadProgress}%</> : <><i className="fas fa-upload mr-1" /> Subir Fotos</>}
            </Button>
            <button onClick={() => { setShowConfig(!showConfig); if (!showConfig) loadConfig(); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg text-sm transition-colors"
              data-testid="btn-config-galeria">
              <i className="fas fa-cog" />
            </button>
            {config.folder_id && (
              <a href={`https://drive.google.com/drive/folders/${config.folder_id}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="btn-administrar-drive">
                <i className="fab fa-google-drive" /> Administrar en Drive
              </a>
            )}
          </div>
        )}
      </div>

      {/* New folder at root */}
      {showNewFolder && isAdmin && (
        <div className="flex items-center gap-2 mb-4 bg-blue-50 p-3 rounded-lg" data-testid="new-folder-root-input">
          <i className="fas fa-folder-plus text-blue-500" />
          <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Nombre del álbum..." className="flex-1 px-3 py-1.5 border rounded-lg text-sm"
            onKeyDown={(e) => e.key === 'Enter' && crearCarpeta()} data-testid="input-nombre-album" />
          <Button size="sm" onClick={crearCarpeta}>Crear</Button>
          <Button size="sm" variant="ghost" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>Cancelar</Button>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="mb-4 bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm text-blue-700 mb-2">
            <i className="fas fa-cloud-upload-alt" /> Subiendo fotos... {uploadProgress}%
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      {/* Config panel */}
      {showConfig && isAdmin && (
        <Card className="mb-5 border-amber-200 bg-amber-50" data-testid="config-panel">
          <CardContent className="p-5">
            <h3 className="font-semibold text-amber-800 mb-3"><i className="fas fa-cog mr-2" />Configuración de Google Drive</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key de Google</label>
                <input type="password" value={config.api_key} onChange={(e) => setConfig({...config, api_key: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="AIzaSy..." data-testid="input-api-key" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID de Carpeta de Google Drive</label>
                <input type="text" value={config.folder_id} onChange={(e) => setConfig({...config, folder_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="1qKNQl..." data-testid="input-folder-id" />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={saveConfig} disabled={savingConfig} size="sm" data-testid="btn-guardar-config">
                  {savingConfig ? <><i className="fas fa-spinner fa-spin mr-1" /> Guardando...</> : <><i className="fas fa-save mr-1" /> Guardar</>}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowConfig(false)}>Cancelar</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Not configured warning */}
      {noConfigurado && isAdmin && !showConfig && (
        <Card className="mb-5 border-red-200 bg-red-50" data-testid="aviso-no-configurado">
          <CardContent className="p-5 text-center">
            <i className="fas fa-exclamation-triangle text-3xl text-red-400 mb-2" />
            <h3 className="font-semibold text-red-800 mb-1">Google Drive no configurado</h3>
            <p className="text-sm text-red-600 mb-3">Configurá la API Key y el ID de carpeta para activar la galería</p>
            <Button size="sm" onClick={() => { setShowConfig(true); loadConfig(); }} data-testid="btn-configurar">
              <i className="fas fa-cog mr-1" /> Configurar ahora
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Albums grid */}
      {albumes.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {albumes.map((album) => (
            <Card key={album.id} className="group overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              onClick={() => abrirAlbum(album.id, album.nombre)} data-testid={`album-${album.id}`}>
              <div className="relative h-48 bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
                {album.portada_url ? (
                  <img src={album.portada_url} alt={album.nombre}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="fas fa-folder text-5xl text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-bold text-lg leading-tight">{album.nombre}</h3>
                  <p className="text-white/70 text-sm mt-1"><i className="fas fa-image mr-1" /> {album.num_fotos} fotos</p>
                </div>
                <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-medium">
                  {album.fecha ? new Date(album.fecha).toLocaleDateString('es-UY', { month: 'short', year: 'numeric' }) : ''}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Root photos */}
      {fotosRaiz.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-3"><i className="fas fa-image mr-2 text-purple-500" />Fotos</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {fotosRaiz.map((foto) => (
              <div key={foto.id}
                className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer bg-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                onClick={() => setFotoSeleccionada(foto)} data-testid={`foto-raiz-${foto.id}`}>
                <img src={foto.thumbnail_url} alt={foto.nombre}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-xs truncate">{foto.nombre}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {albumes.length === 0 && fotosRaiz.length === 0 && !noConfigurado && (
        <Card><CardContent className="p-12 text-center">
          <i className="fas fa-folder-open text-5xl text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-500 mb-1">No hay fotos todavía</h3>
          <p className="text-sm text-gray-400">Usá los botones de arriba para crear álbumes y subir fotos</p>
        </CardContent></Card>
      )}
    </div>
  );
};

export default GaleriaFotos;
